import { listingTypesMasterJson } from "../../constants/ListingTypesDefs";
import {
  ListingTypesMaster,
  ListingTypeConfig,
  ListingTypeVariantConfig,
  StrategicParameters,
  SharedAttributeConfig,
  CategoryAttributeConfig,
} from "../../interfaces/ProductObject";

// ─── Label ↔ ID mapping (keeps existing Redux/API values unchanged) ────────────
// The config uses snake_case IDs; the Redux slice and API payload use legacy labels.

const LABEL_TO_ID: Record<string, string> = {
  "New Product": "new_product",
  "Used Product": "used_product",
  Menu: "menu",
  "Menu Item": "menu",
  Rentals: "rental",
  Rental: "rental",
  "Real Estate": "real_estate",
  Service: "service",
  Info: "info",
  Other: "others",
  Others: "others",
};

const ID_TO_LABEL: Record<string, string> = {
  new_product: "New Product",
  used_product: "Used Product",
  menu: "Menu Item",
  rental: "Rental",
  real_estate: "Real Estate",
  service: "Service",
  others: "Other",
};

class CreateProductConfigHelper {
  // ─── Master config accessor ────────────────────────────────────────────────

  public static getListingTypesMaster(
    uiConfigurations: any,
  ): ListingTypesMaster {
    // Try remote config first
    const remoteRaw = uiConfigurations?.listing_types_master?.configValue;
    if (remoteRaw) {
      try {
        const parsed =
          typeof remoteRaw === "string" ? JSON.parse(remoteRaw) : remoteRaw;
        if (parsed?.listing_types?.length) return parsed as ListingTypesMaster;
      } catch (e) {
        console.error(
          "[CreateProductConfigHelper] Failed to parse remote listing_types_master",
          e,
        );
      }
    }

    // Fallback to local constant
    try {
      return JSON.parse(listingTypesMasterJson) as ListingTypesMaster;
    } catch (e) {
      console.error(
        "[CreateProductConfigHelper] Failed to parse local listing_types_master",
        e,
      );
      // Last-resort empty master so the UI never crashes
      return {
        listing_types: [],
        shared_attributes: [],
        form_sections: [],
      } as any;
    }
  }

  // ─── Single listing type config ────────────────────────────────────────────

  public static getListingTypeConfig(
    master: ListingTypesMaster,
    listingTypeId: string,
  ): ListingTypeConfig | null {
    if (!listingTypeId) return null;
    return master.listing_types.find((lt) => lt.id === listingTypeId) ?? null;
  }

  // ─── Media cap ─────────────────────────────────────────────────────────────

  /** Combined media cap used when a listing type has no config. */
  public static readonly DEFAULT_MAX_MEDIA = 5;

  /** Max media items (images + videos + PDFs combined) a listing of this type
   *  may hold. Accepts the legacy label stored on a product ("Used Product")
   *  or the config id ("used_product"). */
  public static getMaxMediaForListingType(
    uiConfigurations: any,
    listingType: string | undefined,
  ): number {
    const master =
      CreateProductConfigHelper.getListingTypesMaster(uiConfigurations);
    const config = CreateProductConfigHelper.getListingTypeConfig(
      master,
      CreateProductConfigHelper.listingTypeLabelToId(listingType ?? ""),
    );
    return (
      config?.media.max_images ?? CreateProductConfigHelper.DEFAULT_MAX_MEDIA
    );
  }

  // ─── Shared attributes for a listing type ─────────────────────────────────

  public static getSharedAttributesForType(
    master: ListingTypesMaster,
    listingTypeId: string,
  ): SharedAttributeConfig[] {
    if (!listingTypeId) return [];
    return (master.shared_attributes ?? []).filter((attr) =>
      attr.listing_types.includes(listingTypeId),
    );
  }

  // ─── Category attributes filtered by listing type ─────────────────────────

  public static getCategoryAttributesForType(
    categoryAttributes: CategoryAttributeConfig[],
    listingTypeId: string,
  ): CategoryAttributeConfig[] {
    if (
      !categoryAttributes ||
      !Array.isArray(categoryAttributes) ||
      !listingTypeId
    )
      return [];
    return categoryAttributes.filter(
      (attr) =>
        !attr.listing_types ||
        attr.listing_types.length === 0 ||
        attr.listing_types.includes(listingTypeId),
    );
  }

  // ─── Label ↔ ID conversion ─────────────────────────────────────────────────

  public static listingTypeLabelToId(label: string): string {
    const trimmed = label?.trim() ?? "";
    const normalized = trimmed.toLowerCase().replace(/[\s-]+/g, "_");
    return (
      LABEL_TO_ID[trimmed] ??
      (normalized === "rentals"
        ? "rental"
        : normalized === "menu_item"
          ? "menu"
          : normalized)
    );
  }

  public static listingTypeIdToLabel(id: string): string {
    const trimmed = (id ?? "").trim();
    return ID_TO_LABEL[trimmed] ?? trimmed;
  }

  // ─── Condition value helpers ───────────────────────────────────────────────
  // Config uses snake_case (like_new), display label is "Like New".
  // The API payload receives the config value directly.

  public static getConditionOptions(
    master: ListingTypesMaster,
  ): Array<{ value: string; label: string; hint?: string }> {
    const conditionAttr = (master.shared_attributes ?? []).find(
      (attr) => attr.name === "condition",
    );
    if (!conditionAttr?.options) return [];
    // options can be string[] or {value,label,hint}[]
    return (conditionAttr.options as any[]).map((opt) =>
      typeof opt === "string" ? { value: opt, label: opt } : opt,
    );
  }

  // ─── Variants: single source of truth ──────────────────────────────────────
  // Three places used to each decide "are variants active" with slightly
  // different rules (the listing-type config, the strategy toggle, whether
  // rows exist), which could disagree with each other after a remote config
  // change or a stale redux value. These two helpers are now the only place
  // that decision is made.

  /** "Variants mode" is on for this listing type + draft: the config allows
   *  variants AND the seller has the toggle on. Used to decide whether to
   *  show the variant-rows editor (vs. a plain price field) and whether to
   *  validate variant rows at all. Deliberately does NOT require rows to
   *  exist yet, so "mode on, zero rows" is still recognised as mode-on and
   *  can be flagged as an error rather than silently skipped. */
  public static isVariantsModeEnabled(
    variantsConfig: ListingTypeVariantConfig | null | undefined,
    strategy: StrategicParameters,
  ): boolean {
    return !!variantsConfig?.enabled && !!strategy.variantsEnabled;
  }

  /** Variants are actually active for the payload: mode is on AND at least
   *  one variant row exists. This is stricter than isVariantsModeEnabled on
   *  purpose — an empty-rows draft should never reach the backend as
   *  hasVariants:true. */
  public static areVariantsActive(
    variantsConfig: ListingTypeVariantConfig | null | undefined,
    strategy: StrategicParameters,
  ): boolean {
    return (
      CreateProductConfigHelper.isVariantsModeEnabled(
        variantsConfig,
        strategy,
      ) && (strategy.variantDrafts?.length ?? 0) > 0
    );
  }
}

export default CreateProductConfigHelper;
